import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress 
} from '@mui/material';
import { 
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';

/**
 * DaysToReleaseInfo component for displaying days remaining until release
 * @param {Object} props - Component props
 * @param {Object} props.releaseData - Release data object
 * @returns {JSX.Element} DaysToReleaseInfo component
 */
const DaysToReleaseInfo = ({ releaseData }) => {
  // Calculate business days between two dates (excluding weekends)
  const calculateBusinessDays = (startDate, endDate) => {
    // Copy dates to avoid modifying the originals
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Reset time part to compare just the dates
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    // If the start date is after the end date, return negative business days
    if (start > end) {
      return -calculateBusinessDays(end, start);
    }
    
    // If same date, return 0 business days
    if (start.getTime() === end.getTime()) {
      return 0;
    }
    
    let businessDays = 0;
    const currentDate = new Date(start);
    
    // Loop through each day from start to end
    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
      
      // Move to the next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return businessDays;
  };
  
  // Calculate days to release (excluding weekends)
  const calculateDaysToRelease = () => {
    if (!releaseData || !releaseData.releaseDate) {
      return null;
    }
    
    const releaseDate = new Date(releaseData.releaseDate);
    const today = new Date();
    
    // Reset time part to compare just the dates
    today.setHours(0, 0, 0, 0);
    releaseDate.setHours(0, 0, 0, 0);
    
    // If release date is in the past, return negative days
    if (releaseDate < today) {
      return -1; // Just indicate it's past due, exact days not important
    }
    
    // Calculate business days between today and release date
    return calculateBusinessDays(today, releaseDate);
  };
  
  const daysToRelease = calculateDaysToRelease();
  
  // Determine color based on days remaining
  const getColor = (days) => {
    if (days === null) return '#9e9e9e'; // Grey for unknown
    if (days < 0) return '#9e9e9e';      // Grey for past due
    if (days < 3) return '#f44336';      // Red for < 3 days
    if (days < 7) return '#ffc107';      // Amber for 3-6 days
    return '#4caf50';                    // Green for >= 7 days
  };
  
  const color = getColor(daysToRelease);
  
  // Calculate progress percentage for circular progress
  const calculateProgress = (days) => {
    if (days === null) return 0;
    if (days < 0) return 100;
    
    // Assume 30 days is 0% and 0 days is 100%
    const progress = 100 - Math.min(100, (days / 30) * 100);
    return progress;
  };
  
  const progress = calculateProgress(daysToRelease);
  
  // Get release date formatted
  const getFormattedReleaseDate = () => {
    if (!releaseData || !releaseData.releaseDate) {
      return 'Not scheduled';
    }
    
    return new Date(releaseData.releaseDate).toLocaleDateString();
  };
  
  // Get status message
  const getStatusMessage = (days) => {
    if (days === null) return 'Release date not set';
    if (days < 0) return 'Release date has passed';
    if (days === 0) return 'Release is today!';
    if (days === 1) return 'Release is tomorrow';
    return `${days} business days to release`;
  };
  
  return (
    <Box height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
      <Typography variant="h6" gutterBottom align="center">
        Release Countdown
      </Typography>
      
      <Box position="relative" display="inline-flex" mb={2}>
        <CircularProgress
          variant="determinate"
          value={progress}
          size={120}
          thickness={5}
          sx={{ color: color }}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          {daysToRelease !== null ? (
            <Typography variant="h4" component="div" color={color} fontWeight="bold">
              {daysToRelease < 0 ? '0' : daysToRelease}
            </Typography>
          ) : (
            <Typography variant="h4" component="div" color={color} fontWeight="bold">
              ?
            </Typography>
          )}
          <Typography variant="caption" component="div" color="text.secondary">
            days
          </Typography>
        </Box>
      </Box>
      
      <Box display="flex" alignItems="center" mb={1}>
        <CalendarIcon sx={{ mr: 1, color: color }} />
        <Typography variant="body1">
          {getFormattedReleaseDate()}
        </Typography>
      </Box>
      
      <Typography variant="body2" color="text.secondary" align="center">
        {getStatusMessage(daysToRelease)}
      </Typography>
      
      {releaseData && releaseData.version && (
        <Typography variant="body1" fontWeight="bold" mt={2} align="center">
          {releaseData.version}
        </Typography>
      )}
    </Box>
  );
};

export default DaysToReleaseInfo;
